/**
 * Settled Loans — Phase 1 core record UI.
 *
 * Layout (mirrors the Lead Nurture aesthetic):
 *  - Header w/ stats chips (Total / Fixed / Book value / Mine)
 *  - Filter bar (search + lender + rate type + lifecycle status)
 *  - Loan table with key fields + click-row to open the right-side drawer
 *  - "New loan" button opens the create form (modal-like sheet)
 *
 * Phase 1 scope: create, view, edit, search. Document upload + CSV import
 * land in the next pass; auto-care-cycle is Phase 2.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Search, X, Loader2, Building2, DollarSign, Flame, User as UserIcon, ListChecks, AlertTriangle, Zap, ShieldAlert, Target, FileText, BookOpen } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import LoanDrawer from '../components/settled_loans/LoanDrawer';
import LoanCreateForm from '../components/settled_loans/LoanCreateForm';
import CareQueueTab from '../components/settled_loans/CareQueueTab';
import NoContactTab from '../components/settled_loans/NoContactTab';
import ActionWeekTab from '../components/settled_loans/ActionWeekTab';
import ExceptionsTab from '../components/settled_loans/ExceptionsTab';
import OpportunitiesTab from '../components/settled_loans/OpportunitiesTab';
import TemplatesTab from '../components/settled_loans/TemplatesTab';
import InstructionsTab from '../components/settled_loans/InstructionsTab';

const fmtMoney = (n) => {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
};

const SettledLoans = () => {
  const { token, user } = useAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [loans, setLoans] = useState([]);
  const [stats, setStats] = useState(null);
  const [careStats, setCareStats] = useState(null);
  const [excStats, setExcStats] = useState(null);
  const [oppStats, setOppStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [rateFilter, setRateFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [openLoanId, setOpenLoanId] = useState(null);
  const [tab, setTab] = useState('loans'); // loans | care | nocontact

  // "How to use" first-visit nudge. Persisted in localStorage per user so
  // returning users aren't pestered. Cleared when they first visit the tab.
  const tourKey = useMemo(() => `derwent.settled_loans_tour_seen.${user?.id || 'anon'}`, [user?.id]);
  const [tourSeen, setTourSeen] = useState(() => {
    try { return localStorage.getItem(tourKey) === '1'; } catch { return true; }
  });
  const markTourSeen = useCallback(() => {
    if (tourSeen) return;
    try { localStorage.setItem(tourKey, '1'); } catch { /* ignore quota */ }
    setTourSeen(true);
  }, [tourKey, tourSeen]);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadStats = useCallback(async () => {
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        fetch(`${backendUrl}/api/settled-loans/stats`, { headers: authHeaders }),
        fetch(`${backendUrl}/api/settled-loans/care/stats`, { headers: authHeaders }),
        fetch(`${backendUrl}/api/settled-loans/exceptions/stats`, { headers: authHeaders }),
        fetch(`${backendUrl}/api/settled-loans/opportunities/stats`, { headers: authHeaders }),
      ]);
      if (r1.ok) setStats(await r1.json());
      if (r2.ok) setCareStats(await r2.json());
      if (r3.ok) setExcStats(await r3.json());
      if (r4.ok) setOppStats(await r4.json());
    } catch (_e) { /* ignore */ }
  }, [backendUrl, authHeaders]);

  const loadLoans = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (rateFilter !== 'all') params.set('rate_type', rateFilter);
      const r = await fetch(`${backendUrl}/api/settled-loans?${params}`, { headers: authHeaders });
      if (r.ok) setLoans(await r.json());
    } catch (_e) { /* ignore */ } finally { setLoading(false); }
  }, [backendUrl, authHeaders, search, rateFilter]);

  useEffect(() => {
    loadStats();
    loadLoans();
  }, [loadStats, loadLoans]);

  const onCreated = (newLoan) => {
    setShowCreate(false);
    setLoans((prev) => [newLoan, ...prev]);
    setOpenLoanId(newLoan.id);
    loadStats();
    toast.success(`Loan ${newLoan.loan_number} created for ${newLoan.client_name}`);
  };

  const onUpdated = () => {
    loadLoans();
    loadStats();
  };

  return (
    <div className="min-h-screen bg-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Title */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-slate-400 mb-1">
              Post-Settlement · Loan Book
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white">
              Settled <span className="text-blue-600">Loans</span>
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Every loan after settlement — the source of truth for retention, reviews and trail.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold shadow transition-all"
            data-testid="settled-loans-new-btn"
          >
            <Plus className="w-4 h-4" /> New loan record
          </button>
        </div>

        {/* Stat chips */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <StatChip icon={Building2} label="Loans on book" value={stats.total_loans} accent="slate" />
            <StatChip icon={Flame} label="Fixed loans" value={stats.fixed_loans} accent="amber" />
            <StatChip icon={DollarSign} label="Book value" value={fmtMoney(stats.total_book_value)} accent="emerald" />
            <StatChip icon={UserIcon} label="Mine" value={stats.mine} accent="blue" />
          </div>
        )}

        {/* Phase 2 + 3 tabs */}
        <div className="flex gap-1 mb-4 border-b border-slate-200 dark:border-slate-800 overflow-x-auto">
          {[
            { k: 'loans',      label: 'Loan book',        icon: Building2,      badge: stats?.total_loans },
            { k: 'action',     label: 'Action this week', icon: Zap,            badge: null },
            { k: 'opps',       label: 'Opportunities',    icon: Target,         badge: oppStats?.open_total },
            { k: 'care',       label: 'Care queue',       icon: ListChecks,     badge: careStats ? (careStats.overdue_mine + careStats.due_week_mine) : null, urgent: !!careStats?.overdue_mine },
            { k: 'nocontact',  label: 'No-contact',       icon: AlertTriangle,  badge: careStats?.stale_loans, urgent: !!careStats?.stale_loans },
            { k: 'exceptions', label: 'Exceptions',       icon: ShieldAlert,    badge: excStats?.open_total, urgent: !!excStats?.open_total },
            { k: 'templates',  label: 'Templates',        icon: FileText,       badge: null },
            { k: 'help',       label: 'How to use',       icon: BookOpen,       badge: null, tour: !tourSeen },
          ].map(({ k, label, icon: TabIcon, badge, urgent, tour }) => (
            <button
              key={k}
              type="button"
              onClick={() => { setTab(k); if (k === 'help') markTourSeen(); }}
              className={`relative inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${
                tab === k
                  ? 'border-blue-600 text-blue-700 dark:text-blue-300'
                  : 'border-transparent text-slate-500 hover:text-slate-900 dark:hover:text-white'
              }`}
              data-testid={`settled-loans-tab-${k}`}
            >
              <TabIcon className="w-4 h-4" />
              {label}
              {tour && (
                <span className="relative inline-flex ml-1" data-testid="help-tab-tour-pulse" aria-label="New — take the tour">
                  <span className="absolute inset-0 rounded-full bg-emerald-500 opacity-60 animate-ping" />
                  <span className="relative inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-500 text-white text-[9px] font-bold">1</span>
                </span>
              )}
              {(badge !== null && badge !== undefined && badge > 0) && (
                <span className={`ml-1 px-1.5 rounded-full text-[10px] font-bold ${
                  urgent ? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300' : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'
                }`}>{badge}</span>
              )}
            </button>
          ))}
        </div>

        {tab === 'action' ? (
          <ActionWeekTab onOpenLoan={(id) => setOpenLoanId(id)} />
        ) : tab === 'opps' ? (
          <OpportunitiesTab onOpenLoan={(id) => setOpenLoanId(id)} />
        ) : tab === 'care' ? (
          <CareQueueTab onOpenLoan={(id) => setOpenLoanId(id)} />
        ) : tab === 'nocontact' ? (
          <NoContactTab onOpenLoan={(id) => setOpenLoanId(id)} onLoanTouched={() => loadStats()} />
        ) : tab === 'exceptions' ? (
          <ExceptionsTab onOpenLoan={(id) => setOpenLoanId(id)} />
        ) : tab === 'templates' ? (
          <TemplatesTab />
        ) : tab === 'help' ? (
          <InstructionsTab />
        ) : (
        <>
        {/* Filter bar */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-3 mb-4 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, phone, email, loan number, lender…"
              className="w-full pl-10 pr-9 py-2 rounded-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm focus:outline-none focus:border-blue-400"
              data-testid="settled-loans-search"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex gap-1.5">
            {[
              { k: 'all', label: 'All' },
              { k: 'Fixed', label: 'Fixed' },
              { k: 'Variable', label: 'Variable' },
              { k: 'Split', label: 'Split' },
            ].map(({ k, label }) => (
              <button
                key={k}
                type="button"
                onClick={() => setRateFilter(k)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  rateFilter === k
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-slate-400'
                }`}
                data-testid={`settled-loans-rate-${k}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
            </div>
          ) : loans.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-500">
              <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="font-semibold text-slate-700 dark:text-slate-200 mb-1">No loans yet</p>
              <p className="text-xs">Create the first record above — once settled, drop it in here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800">
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    <th className="px-4 py-3">Loan #</th>
                    <th className="px-4 py-3">Client</th>
                    <th className="px-4 py-3">Lender</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                    <th className="px-4 py-3">Rate</th>
                    <th className="px-4 py-3">Fixed Expiry</th>
                    <th className="px-4 py-3">Settled</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {loans.map((l) => (
                    <tr
                      key={l.id}
                      onClick={() => setOpenLoanId(l.id)}
                      className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                      data-testid="settled-loans-row"
                    >
                      <td className="px-4 py-3 font-mono text-xs text-blue-700 dark:text-blue-300 font-bold">
                        {l.loan_number}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900 dark:text-white flex items-center gap-1.5">
                          {l.client_name}
                          {(l.enrolment_state || 'active') === 'draft' && (
                            <span className="px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold tracking-wide bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300" title="Awaiting enrolment approval">Draft</span>
                          )}
                          {l.do_not_contact && (
                            <span className="px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold tracking-wide bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300" title="Do not contact">DNC</span>
                          )}
                          {l.deceased && (
                            <span className="px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold tracking-wide bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300" title="Deceased">†</span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500">{l.client_phone || l.client_email || ''}</div>
                      </td>
                      <td className="px-4 py-3">{l.lender}</td>
                      <td className="px-4 py-3 text-right font-mono">{fmtMoney(l.loan_amount)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                          l.rate_type === 'Fixed'
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                            : l.rate_type === 'Split'
                            ? 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-300'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                        }`}>
                          {l.rate_type} · {Number(l.interest_rate).toFixed(2)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs">{l.fixed_rate_expiry || '—'}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{l.settlement_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
        )}
      </div>

      {/* Create form (full-screen sheet on mobile) */}
      {showCreate && (
        <LoanCreateForm
          onClose={() => setShowCreate(false)}
          onCreated={onCreated}
        />
      )}

      {/* Loan drawer */}
      {openLoanId && (
        <LoanDrawer
          loanId={openLoanId}
          onClose={() => setOpenLoanId(null)}
          onUpdated={onUpdated}
        />
      )}
    </div>
  );
};

const StatChip = ({ icon: Icon, label, value, accent }) => {
  const accents = {
    slate:   'text-slate-700 dark:text-slate-200',
    amber:   'text-amber-700 dark:text-amber-300',
    emerald: 'text-emerald-700 dark:text-emerald-300',
    blue:    'text-blue-700 dark:text-blue-300',
  };
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-3 flex items-center gap-3">
      <div className={`h-9 w-9 rounded-xl bg-slate-100 dark:bg-slate-800 grid place-items-center ${accents[accent]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-semibold">{label}</div>
        <div className={`text-lg font-bold leading-tight ${accents[accent]}`}>{value}</div>
      </div>
    </div>
  );
};

export default SettledLoans;
